/**
 * ToastHost — mounts <ToastViewport> and registers the singleton sink
 * so `toast({tone, message})` calls from anywhere in the app land here.
 *
 * Mount once near the top of the tree (Layout). Multiple mounts will
 * fight over the active sink — the last mount wins.
 */
import { useCallback, useEffect, useState } from 'react';

import { ToastViewport, type ToastVariant } from './Toast';
import { registerToastSink, type ToastInput } from '@/lib/toast';

interface InternalToast {
  id: string;
  tone: ToastVariant;
  message: string;
}

function newId(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID();
  }
  return `toast-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

export function ToastHost(): JSX.Element {
  const [toasts, setToasts] = useState<InternalToast[]>([]);

  const dismiss = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  useEffect(() => {
    registerToastSink((input: ToastInput) => {
      setToasts((prev) => [
        ...prev,
        { id: newId(), tone: input.tone as ToastVariant, message: input.message },
      ]);
    });
    return () => registerToastSink(null);
  }, []);

  return (
    <ToastViewport
      toasts={toasts.map((t) => ({
        id: t.id,
        variant: t.tone,
        title: t.message,
        onDismiss: dismiss,
      }))}
    />
  );
}
